import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { showModal } from 'modal.react.js';
import { mergeDeepRight, omit } from 'ramda';
import { useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import { ISelectOption } from 'components/shared/BaseFormComponents/Select/types';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { EActivityEventType, ETripEvent } from 'detailsv2/structure';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { activityLoadTypes } from '../../../../../../../constants';
import { getOptionsLoader } from '../../../../functions';
import { useChangePagePopover, useDefaultValues, useTripEventSaveInfo } from '../../../shared';
import { useActivityWizardContext } from '../../context';
import { activityValidationSchema } from '../../validation';
import * as types from './types';
import { FieldValues } from './types';

export function useActivityForm() {
  const { tripEvent, data, updateData, nextStep, onSubmit } = useActivityWizardContext();
  const organizedBy = useSelector(itineraryScheduleTypeSelector);

  const filteredStepData = omit(['prevAttachedPage'], data.default); // remove unexpected backend data
  const defaultValues = mergeDeepRight(tripEvent, filteredStepData);
  const methods = useForm<types.FieldValues>({
    defaultValues: {
      ...defaultValues,
      data: {
        ...defaultValues.data,
        type: defaultValues.data.type || EActivityEventType.CustomTime,
      },
      attachedPage: defaultValues?.attachedPages?.[0],
    },
    resolver: yupResolver(activityValidationSchema(organizedBy)),
    mode: 'onTouched',
  });

  const handleResetData = React.useCallback(
    (currentData: FieldValues, newPage: { name?: string | null }) => ({
      data: {
        type: EActivityEventType.CustomTime,
        isCollapsible: false,
        details: null,
        confirmationNumber: '',
        contactInfo: '',
        website: '',
        location: '',
        title: newPage.name || '',
        time: { start: '', end: '' },
        notes: null,
      },
    }),
    [],
  );

  const { handleAttachedPage, keepData, resetData, setProceedStatus } = useDefaultValues({
    methods,
    onResetData: handleResetData,
    eventType: ETripEvent.Activity,
  });

  const { onChangePage, popover } = useChangePagePopover({
    keepData,
    resetData,
    setProceedStatus,
    handleAttachedPage,
    methods,
    prevAttachedPage: data.default.prevAttachedPage,
  });

  const { saveInfoSubmitHandler, isSafeInfoDisabled, safeInfoTooltipId } = useTripEventSaveInfo<FieldValues>({
    eventType: ETripEvent.Activity,
    methods,
    handleSubmit: onSubmit,
  });

  const loadOptions = getOptionsLoader(activityLoadTypes, createOptionFromPageV2);

  const { watch, setValue, getValues, trigger } = methods;
  const isMultiDayActivity = watch('data.type') === EActivityEventType.MultiDay;
  const attachedPage = watch('attachedPage');
  const isVisibleFormBody = !!attachedPage;
  const isNeedToShowTooltip =
    tripEvent.id && tripEvent.showChangedRoomOrPageTooltip && tripEvent?.attachedPages?.[0]?.id !== attachedPage?.id;

  /* Call onChangePage after chose "one-off event" (or click "back" button) on creation new event in builder */
  React.useEffect(() => {
    if (attachedPage?.isNew) {
      onChangePage(attachedPage.name, attachedPage.id);
    }
  }, []);

  React.useEffect(() => {
    trigger('datesRange');
    if (!isMultiDayActivity) {
      setValue('datesRange', { start: getValues('datesRange.start'), end: null });
      setValue('daysRange', { start: getValues('daysRange.start'), end: null });
    }
  }, [isMultiDayActivity, setValue, getValues, trigger]);

  const onCreateOption = async (inputValue: ISelectOption) => {
    const attachedPage = {
      id: inputValue.id?.toString() || undefined,
      name: inputValue.name,
    };
    updateData('default', {
      ...getValues(),
      attachedPages: [attachedPage],
      prevAttachedPage: getValues('attachedPage'),
    });
    nextStep();
  };

  const openPagePreview = async (pageId: number) => {
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} />
      </Modal>
    ));
  };

  return {
    loadOptions,
    isMultiDayActivity,
    methods,
    openPagePreview,
    isVisibleFormBody,
    isNeedToShowTooltip,
    onCreateOption,
    tripEventDeafultValuesController: {
      isSafeInfoDisabled,
      safeInfoTooltipId,
      saveInfoSubmitHandler,
    },
    onChangePage,
    popover,
  };
}

export const useActivityFormContext = () => {
  const methods = useFormContext<types.FieldValues>();
  return methods;
};
